import { useCallback, useEffect, useRef, useState } from "react";
import { usePermissions } from "@/hooks/usePermissions";
import { useNavigate, useOutletContext } from "react-router";
import { useReducedMotion } from "motion/react";
import { Menu, Search, SlidersHorizontal, Inbox, TriangleAlert } from "lucide-react";
import { EmptyState, ErrorState, FilteredEmptyState } from "@/app/routes/dashboard/components/ViewStates";
import { queue } from "@/components/Toast";
import type { CockpitContext } from "@/app/routes/dashboard/CockpitLayout";
import type { Trend, TrendType } from "@/types/trend.type";
import {
  REFERENCES_PATH,
  saveErrorMessage,
  showAlreadySaved,
  useSavedTrendIds,
  useSaveTarget,
  useSaveTrendToReferences,
} from "../api/useSaveTrendToReferences";
import { recreatePrompt } from "../components/trendMeta";
import type { TrendFilterGroup } from "../trendFilters";
import PhoneFeedItem from "./PhoneFeedItem";
import TrendFilterSheet from "./TrendFilterSheet";
import { usePhoneLaneSlotRef } from "@/app/routes/dashboard/agent/phoneLaneSlot";
import "./phoneFeed.css";

/** The share of an item that has to be on screen for it to be the one playing. */
const ACTIVE_RATIO = 0.6;
/** Ask for the next page this many items before the end. */
const PREFETCH_WITHIN = 3;
/** A tap on the stage shows the new state for this long. */
const GLYPH_MS = 650;

/**
 * TRENDS ON A PHONE (under 768px): one trend per screen, swiped up.
 *
 * Its own top bar (the shell's floating menu trigger is hidden here, and this
 * bar's Menu opens the same drawer), then a vertical snap feed. The item that
 * is 60% or more on screen plays, muted until the viewer turns sound on, and
 * sound stays on as they swipe on. A tap on the stage pauses or plays it, with
 * a brief glyph. Under reduced motion nothing starts by itself, and a tap
 * plays. The next page is asked for three items before the end.
 *
 * The filters are the desktop's own groups and state, in a bottom sheet.
 */
export default function TrendsPhoneFeed({
  rows,
  hasMore,
  isLoading,
  isFetchingMore,
  isError,
  onRetry,
  failed,
  moreError,
  onRetryMore,
  onLoadMore,
  resetKey,
  filterGroups,
  anyFilter,
  types,
  onClearAll,
  filtered,
  onClearEverything,
  query,
  onQueryChange,
  onSubmitSearch,
  onCancelSearch,
  lastSeenMs,
  detailOpen,
  onOpenDetail,
  live,
}: {
  rows: Trend[];
  hasMore: boolean;
  isLoading: boolean;
  isFetchingMore: boolean;
  isError: boolean;
  onRetry: () => void;
  /** Merged reads that did not answer while others did. */
  failed: number;
  /** A page after the first did not load: the feed ends on a retry. */
  moreError: boolean;
  onRetryMore: () => void;
  onLoadMore: () => void;
  /** Changes whenever the question changes, which starts the feed over. */
  resetKey: string;
  filterGroups: readonly TrendFilterGroup[];
  anyFilter: boolean;
  types: readonly TrendType[];
  onClearAll: () => void;
  /** Filters or a search narrow the feed. */
  filtered: boolean;
  /** Clear the filters and the search, from the empty state. */
  onClearEverything: () => void;
  query: string;
  onQueryChange: (next: string) => void;
  onSubmitSearch: () => void;
  onCancelSearch: () => void;
  lastSeenMs: number | null;
  /** A trend's detail is open over the feed: nothing here plays. */
  detailOpen: boolean;
  onOpenDetail: (id: string) => void;
  /** Save may talk to the API (false in the fixture harness). */
  live: boolean;
}) {
  const cockpit = useOutletContext<CockpitContext | undefined>();
  const navigate = useNavigate();
  const reduced = useReducedMotion();
  const feedRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const [soundOn, setSoundOn] = useState(false);
  const [tap, setTap] = useState<{ id: string; playing: boolean } | null>(null);
  const [glyph, setGlyph] = useState<{ id: string; kind: "play" | "pause" } | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const laneSlotRef = usePhoneLaneSlotRef();
  const [sheetOpen, setSheetOpen] = useState(false);
  const showSearch = searchOpen || query !== "";

  const save = useSaveTrendToReferences();
  const savedIds = useSavedTrendIds();
  const target = useSaveTarget();
  const canSave = live && target !== null;
  const { canUseWorkshop } = usePermissions();

  /* A new question starts the feed over, from its first trend. */
  const [feedFor, setFeedFor] = useState(resetKey);
  if (feedFor !== resetKey) {
    setFeedFor(resetKey);
    setActive(0);
    setTap(null);
  }
  useEffect(() => {
    feedRef.current?.scrollTo({ top: 0 });
  }, [resetKey]);

  /* A tap's pause or play belongs to that visit to the trend. Moving on clears
     it, so coming back never resumes a trend without a fresh tap (under reduced
     motion that means it stays still), and a newly active trend plays or holds
     by the default for its mode. */
  const [tapActive, setTapActive] = useState(active);
  if (tapActive !== active) {
    setTapActive(active);
    setTap(null);
  }

  /* Which item is 60% or more on screen. Each item's share is kept as it is
     reported, and the most visible one past the line is the one that plays. */
  useEffect(() => {
    const feed = feedRef.current;
    if (!feed || typeof IntersectionObserver === "undefined") return;
    const shares = new Map<number, number>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const index = Number((entry.target as HTMLElement).dataset.index);
          shares.set(index, entry.intersectionRatio);
        }
        let best = -1;
        let bestShare = ACTIVE_RATIO;
        for (const [index, share] of shares) {
          if (share >= bestShare) {
            best = index;
            bestShare = share;
          }
        }
        if (best >= 0) setActive(best);
      },
      { root: feed, threshold: [0, ACTIVE_RATIO, 1] },
    );
    for (const item of feed.querySelectorAll<HTMLElement>(".tf-item")) observer.observe(item);
    return () => observer.disconnect();
  }, [rows.length, resetKey]);

  /* The next page, three items before the end. */
  useEffect(() => {
    if (hasMore && !isFetchingMore && rows.length > 0 && active >= rows.length - PREFETCH_WITHIN) onLoadMore();
  }, [active, rows.length, hasMore, isFetchingMore, onLoadMore]);

  useEffect(() => {
    if (!glyph) return;
    const timer = window.setTimeout(() => setGlyph(null), GLYPH_MS);
    return () => window.clearTimeout(timer);
  }, [glyph]);

  const activeTrend = rows[active];
  const activePlaying =
    !detailOpen &&
    activeTrend !== undefined &&
    (tap?.id === activeTrend.id ? tap.playing : !reduced);

  const onTapStage = useCallback(() => {
    if (!activeTrend) return;
    const next = !activePlaying;
    setTap({ id: activeTrend.id, playing: next });
    setGlyph({ id: activeTrend.id, kind: next ? "play" : "pause" });
  }, [activeTrend, activePlaying]);

  /* Unmuted inside the click, the gesture a browser asks for before a page may
     make a sound; the state follows for every clip after it. */
  const onToggleSound = useCallback(
    (element: HTMLVideoElement | null) => {
      const next = !soundOn;
      if (element) {
        element.muted = !next;
        if (next && activePlaying && element.paused) void element.play().catch(() => {});
      }
      setSoundOn(next);
    },
    [soundOn, activePlaying],
  );

  const onSave = useCallback(
    (trend: Trend) => {
      if (savedIds.includes(trend.id)) {
        showAlreadySaved(() => navigate(REFERENCES_PATH));
        return;
      }
      save.mutateAsync(trend).catch((error: unknown) => {
        queue.add({ title: "Could not save that", description: saveErrorMessage(error), variant: "error" });
      });
    },
    [save, savedIds, navigate],
  );
  const onRecreate = useCallback(
    (trend: Trend) => navigate("/dashboard/workshop", { state: { draftPrompt: recreatePrompt(trend) } }),
    [navigate],
  );

  return (
    <div className="tf-root">
      <header className="tf-bar">
        {showSearch ? (
          <>
            <form
              className="tf-search"
              role="search"
              onSubmit={(event) => {
                event.preventDefault();
                onSubmitSearch();
              }}
            >
              <Search className="size-4 shrink-0 text-co-faint" aria-hidden="true" />
              <input
                className="tf-search-input co-input-own-focus"
                value={query}
                onChange={(event) => onQueryChange(event.target.value)}
                aria-label="Search trends"
                placeholder="Search trends"
                enterKeyHint="search"
                autoFocus={searchOpen}
              />
              <button
                type="button"
                className="tf-search-cancel"
                onClick={() => {
                  setSearchOpen(false);
                  onCancelSearch();
                }}
              >
                Cancel
              </button>
            </form>
            {/* Searching, M7 keeps its own circle at the end of the lane. */}
            <span ref={laneSlotRef} className="tf-lane-slot tf-lane-slot--alone tf-lane-glass" />
          </>
        ) : (
          <>
            <button
              type="button"
              className="tf-icon tf-lane-glass"
              aria-label="Open navigation"
              disabled={!cockpit}
              onClick={(event) => cockpit?.openNav(event.currentTarget)}
            >
              <Menu className="size-5" aria-hidden="true" />
            </button>
            <h1 className="tf-title-bar tf-lane-glass">Trends</h1>
            <div className="tf-bar-end tf-lane-glass">
              <button type="button" className="tf-icon" aria-label="Search trends" onClick={() => setSearchOpen(true)}>
                <Search className="size-5" aria-hidden="true" />
              </button>
              <button
                type="button"
                className="tf-icon"
                aria-label={anyFilter ? "Filters, some set" : "Filters"}
                onClick={() => setSheetOpen(true)}
              >
                <SlidersHorizontal className="size-5" aria-hidden="true" />
                {anyFilter ? <span className="tf-icon-dot" aria-hidden="true" /> : null}
              </button>
              {/* M7 Agents, as the lane's last button rather than a mark
                  floating over this capsule. Empty until the rail fills it. */}
              <span ref={laneSlotRef} className="tf-lane-slot" />
            </div>
          </>
        )}
      </header>

      {/* A partial feed says so, above the feed rather than as one of its
          screens. Plainer than the wall's sentence: the phone prints no total
          for "a floor" to qualify, only which trends it has. */}
      {failed > 0 && !isError && !isLoading ? (
        /* A floating glass note under the top lane, in the toast's grammar
           (library, style B): the attention mark, one sentence, Try again. */
        <div className="tf-notice co-glass-chrome" role="status">
          <TriangleAlert className="tf-notice-mark" aria-hidden="true" />
          <div className="tf-notice-copy">
            <p className="tf-notice-text">Some trends didn't load, so this feed may be missing a few.</p>
            <button type="button" className="tf-notice-retry" onClick={onRetry}>
              Try again
            </button>
          </div>
        </div>
      ) : null}

      <div ref={feedRef} className="tf-feed" aria-busy={isLoading || isFetchingMore}>
        {isLoading ? (
          <div className="tf-item tf-item--skeleton" aria-hidden="true">
            <div className="tf-stage">
              <span className="tf-skeleton-block co-skeleton" />
            </div>
            <div className="tf-strip" />
          </div>
        ) : isError ? (
          <div className="tf-state">
            <ErrorState
              minHeight={0}
              title="The feed did not answer"
              body="The trend library could not be read, so nothing is shown. An empty feed here would be a guess."
              onRetry={onRetry}
            />
          </div>
        ) : rows.length === 0 ? (
          <div className="tf-state">
            {filtered ? (
              <FilteredEmptyState
                minHeight={0}
                title="No trends match"
                body="Nothing in the library fits every filter at once. Widening one beats waiting."
                activeFilters={[]}
                onClear={onClearEverything}
              />
            ) : (
              <EmptyState
                minHeight={0}
                icon={<Inbox aria-hidden />}
                title="Nothing in the library yet"
                body="Nothing has been discovered yet."
              />
            )}
          </div>
        ) : (
          rows.map((trend, index) => (
            <PhoneFeedItem
              key={trend.id}
              trend={trend}
              index={index}
              total={rows.length}
              active={index === active}
              near={Math.abs(index - active) <= 1}
              playing={index === active && activePlaying}
              soundOn={soundOn}
              isNew={lastSeenMs !== null && Date.parse(trend.createdAt) > lastSeenMs}
              saved={savedIds.includes(trend.id)}
              canSave={canSave}
              onToggleSound={onToggleSound}
              onTapStage={onTapStage}
              glyph={glyph && glyph.id === trend.id ? glyph.kind : null}
              onSave={onSave}
              onRecreate={canUseWorkshop ? onRecreate : undefined}
              onDetail={onOpenDetail}
            />
          ))
        )}
        {/* The feed stopped because a page failed, not because it ran out:
            its last screen says so, with a retry for that page. */}
        {!isLoading && !isError && rows.length > 0 && moreError ? (
          <div className="tf-item">
            <div className="tf-state">
              <ErrorState
                minHeight={0}
                title="The next trends did not load"
                body={null}
                onRetry={onRetryMore}
              />
            </div>
          </div>
        ) : null}
      </div>

      <TrendFilterSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        groups={filterGroups}
        canClear={anyFilter}
        onClearAll={onClearAll}
        types={types}
      />
    </div>
  );
}
